// Who a live-picked player is (ids-3). A player picked from the real search is just an id
// in the app (ids-1) — but the picker's label, Undo (which rebuilds a module from that id)
// and the "no data yet" message all need her name, and a player the snapshot doesn't have
// can't be looked up there. So each pick is remembered here, for the page's life (Undo is
// per page too). It holds who she IS, not her data — no API answer is kept for reuse
// (that's Track 3). Plain code, no Amplify: the always-loaded picker imports it, and on
// the snapshot site it simply stays empty.

import type { PlayerId } from "../data/players.js";
import { getPlayerShortName } from "../data/player-utils.js";
import { rememberFamiliarName } from "../data/familiar-names.js";

export interface PickedPlayer {
  id: PlayerId;
  name: string;
  /** Her latest match's team, as the search gave it — null when the DB has none. */
  team: string | null;
  /** The name she's known by ("Lionel Messi"), when the DB has one (familiar-names.ts). */
  nickname?: string | null;
}

const picked = new Map<PlayerId, PickedPlayer>();

export function rememberPickedPlayer(p: PickedPlayer): void {
  picked.set(p.id, p);
  rememberFamiliarName(p.name, p.nickname);
}

/** A remembered pick, or undefined. */
export function pickedPlayer(id: PlayerId): PickedPlayer | undefined {
  return picked.get(id);
}

/** A live pick the snapshot lacks: her short name from the pick itself. A live widget only
 *  ever gets such an id from a pick, so any other id is a bug. */
export function pickedShortName(id: PlayerId | null): string {
  const p = id === null ? undefined : picked.get(id);
  if (!p) throw new Error(`Player id ${String(id)} is neither in the snapshot nor a live pick`);
  return getPlayerShortName(p);
}

/** Every remembered pick that `known` says isn't a snapshot player, oldest first — the
 *  players a new or rebuilt picker must also offer, so Undo / the sticky player can open
 *  a module on her. */
export function pickedOutsideSnapshot(known: (id: PlayerId) => boolean): PickedPlayer[] {
  return [...picked.values()].filter((p) => !known(p.id));
}

/** For tests only. */
export function forgetPickedPlayers(): void {
  picked.clear();
}
